import React, { useState, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { find, map, size, forEach, toNumber, isEmpty, toString } from 'lodash';
import moment from 'moment';
import { Dropdown } from 'react-bootstrap';
import Slider from 'src/components/slider';
import Button from 'src/components/button';
import styles from './styles.module.scss';
import Actions from 'src/components/actionbar';
import DatePicker from 'react-datepicker';
import Select, { MultiValue, SingleValue } from 'react-select';
import { BulkFiltersDefaultValues, dealInsightsTabs, hasDealRoomOptions } from 'src/utils/dealInsights.helper';
import buildUrl from 'build-url-ts';
import { useLocation, useNavigate } from 'react-router-dom';

interface FiltersSliderProps {
  isOpen: boolean;
  filterOptions: Record<string, any>;
  onClose: () => void;
}


interface FieldsProps {
  initialValue: Record<string, any>;
  options: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onValidationChange: (isInvalid: boolean) => void;
}

const Fields: React.FC<FieldsProps> = ({ initialValue, options, onChange, onValidationChange }) => {
  // ui' state's
  const [pipeLineOptions, setPipeLineOptions] = useState<Record<string, any>[]>([]);
  const [dealOwnerOptions, setDealOwnerOptions] = useState<Record<string, any>[]>([]);
  const [dealStagesOptions, setDealStagesOptions] = useState<Record<string, any>[]>([]);

  // min & max range checker
  const isInvalidRange =
    initialValue?.minAmount &&
    initialValue?.maxAmount &&
    Number(initialValue.minAmount) > Number(initialValue.maxAmount);

  // Methods
  useEffect(() => { // set options
    if (options && size(options) > 0) {
      const pipelines = options?.pipelines;
      const owners = options?.owners;

      if (pipelines && size(pipelines) > 0) {
        setPipeLineOptions(pipelines);

        if (!initialValue?.pipelineExternalId) {
          onChange('pipelineExternalId', pipelines[0]?.externalId);
          const stages = pipelines[0]?.stages;
          setDealStagesOptions(stages);
        }

        if (initialValue?.pipelineExternalId) {
          const pipeLine = find(pipelines, (i) => i?.externalId === initialValue?.pipelineExternalId);
          const stages = pipeLine?.stages;
          setDealStagesOptions(stages ?? []);
        }

      }

      if (owners && size(owners) > 0) {
        setDealOwnerOptions(owners);
      }
    }
  }, []);

  useEffect(() => {
    onValidationChange(isInvalidRange ? true : false);
  }, [isInvalidRange])

  const handleClosedDateChange = ({ from, to }: any) => { // closed date
    if (from) {
      onChange('closeDateFrom', from);
      onChange('closeDateTo', null);
    } else if (to) {
      onChange('closeDateTo', to);
    }
  }

  const handleLastActivityDateChange = ({ from, to }: any) => { // last activity date
    if (from) {
      onChange('lastActivityFrom', from);
      onChange('lastActivityTo', null);
    } else if (to) {
      onChange('lastActivityTo', to);
    }
  }

  const handleCreatedDateChange = ({ from, to }: any) => { // created date
    if (from) {
      onChange('createdDateFrom', from);
      onChange('createdDateTo', null);
    } else if (to) {
      onChange('createdDateTo', to);
    }
  }

  const handlePipeLineChange = (val: any) => { // pipeline
    onChange('pipelineExternalId', val?.externalId ?? pipeLineOptions[0]?.externalId);
    setDealStagesOptions(val?.stages ?? pipeLineOptions[0].stages);

    if (!val) {
      onChange('stageExternalId', '');
    }
  }

  const handleDealStageChange = (val: any) => { // stage
    onChange("stageExternalId", val?.externalId ?? '');
  }

  const handleDealOwnerChange = (val: any) => { // owner
    onChange("ownerExternalId", val?.externalId ?? '');
  }

  const handleAmountChange = (e: any, type: string) => { // amount
    const value = e.target.value.replace(/[^0-9]/g, ''); // remove non-numeric
    onChange(type, value);
  }


  return (
    <div className={styles.fields}>

      <div className={styles.formGroup}>
        <p className={styles.label}> CRM Pipeline </p>
        <SingleValueDropDown
          selected={initialValue.pipelineExternalId ? find(pipeLineOptions, (i) => i?.externalId === initialValue.pipelineExternalId) : pipeLineOptions[0]}
          options={pipeLineOptions}
          onChange={handlePipeLineChange}
          loadingOptions={!options}
          getValue={(i) => i?.externalId}
          getLabel={(i) => i?.label}
          placeholder={!options ? 'loading...' : 'select'}
        />
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}> Deal Stages </p>
        <SingleValueDropDown
          selected={find(dealStagesOptions, (i) => i?.externalId === initialValue.stageExternalId)}
          options={dealStagesOptions}
          onChange={handleDealStageChange}
          allowClear
          loadingOptions={size(dealStagesOptions) === 0}
          getValue={(i) => i?.externalId}
          getLabel={(i) => i?.label}
          placeholder={size(dealStagesOptions) === 0 ? 'please select a pipeLine' : 'select'}
        />
        {/* <CustomSelect
          selected={initialValue?.stageExternalId || undefined}
          options={dealStagesOptions}
          isMulti={false}
          allowClear
          onChange={handleDealStageChange}
          getValue={(i) => i.externalId}
          getLabel={(i) => i.label}
          loadingOptions={size(dealStagesOptions) === 0}
          placeholder={size(dealStagesOptions) === 0 ? 'please select a pipeLine' : 'select'}
        /> */}
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}> Deal Owner </p>
        <SingleValueDropDown
          selected={find(dealOwnerOptions, (i) => i?.externalId === initialValue.ownerExternalId)}
          options={dealOwnerOptions}
          onChange={handleDealOwnerChange}
          allowClear
          loadingOptions={!options}
          getValue={(i) => i?.externalId}
          getLabel={(i) => i?.name}
          placeholder={!options ? 'loading...' : 'select'}
        />
        {/* <CustomSelect
          selected={initialValue?.ownerExternalId || undefined}
          options={dealOwnerOptions}
          isMulti={false}
          allowClear
          onChange={handleDealOwnerChange}
          getValue={(i) => i.externalId}
          getLabel={(i) => i.name}
          loadingOptions={isLoadingFilterOptions}
          placeholder={isLoadingFilterOptions ? 'loading...' : 'select'}
        /> */}
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}> Amount (in $)</p>
        <div className={cx(styles.amountGroup, { [styles.errorGroup]: isInvalidRange })}>
          <input
            className={styles.input}
            value={initialValue?.minAmount ?? ''}
            placeholder="Min"
            type='text'
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => handleAmountChange(e, 'minAmount')}
          />
          <input
            className={styles.input}
            value={initialValue?.maxAmount ?? ''}
            type='text'
            placeholder="Max"
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => handleAmountChange(e, 'maxAmount')}
          />
        </div>
        {isInvalidRange && <p className={styles.error}> Invalid range: Min should be less than or equal to Max </p>}
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}>Closed Date</p>
        <DateRangeInput
          from={initialValue?.closeDateFrom ?? null}
          to={initialValue?.closeDateTo ?? null}
          onChange={(val) => handleClosedDateChange(val)}
        />
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}>Last Activity Date</p>
        <DateRangeInput
          from={initialValue?.lastActivityFrom ?? null}
          to={initialValue?.lastActivityTo ?? null}
          onChange={(val) => handleLastActivityDateChange(val)}
        />
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}> Risk Status </p>
        <SingleValueDropDown
          selected={find(dealInsightsTabs, (type) => type.value === (initialValue.riskStatus ?? 'ALL'))}
          options={dealInsightsTabs}
          onChange={(val) => onChange('riskStatus', val ? val.value : '')}
          getValue={(i) => i.value}
          getLabel={(i) => i.label}
        />
      </div>

      <div className={styles.formGroup}>
        <p className={styles.label}> Has DealRoom </p>
        <SingleValueDropDown
          selected={find(hasDealRoomOptions, (type) => type.value === initialValue.hasDealRoom) ?? undefined}
          options={hasDealRoomOptions}
          allowClear
          getValue={(i) => i.value}
          getLabel={(i) => i.label}
          onChange={(val) => onChange('hasDealRoom', val ? val.value : '')}
        />
      </div>

      {/* Created Date */}
      <div className={styles.formGroup}>
        <p className={styles.label}>Created Date</p>
        <DateRangeInput
          from={initialValue?.createdDateFrom ?? null}
          to={initialValue?.createdDateTo ?? null}
          onChange={(val) => handleCreatedDateChange(val)}
        />
      </div>
    </div>
  );
};

export const FiltersSlider: React.FC<FiltersSliderProps> = ({ isOpen, filterOptions, onClose }) => {
  const [values, setValues] = useState<Record<string, number | string | null | boolean>>(BulkFiltersDefaultValues);
  const fieldKeys = Object.keys(BulkFiltersDefaultValues);
  const location = useLocation();
  const navigate = useNavigate();
  const [isInvalid, setIsInvalid] = useState<boolean>(false); // check if the values are invalid

  // Methods
  useEffect(() => { // on open of slider set the values from route
    if (!isOpen) return;

    const searchParams = new URLSearchParams(location.search);
    const existingParams: Record<string, any> = {
      ...BulkFiltersDefaultValues
    };

    fieldKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        existingParams[key] = value;
      }
    });

    setValues(existingParams);
  }, [isOpen]);

  const handleChange = (key: string, value: any) => { // change
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const getFilterParams = (vals: Record<string, any>): Record<string, string> => {
    const obj = {}; // frame a new object
    forEach(Object.entries(vals), ([key, value]) => {
      if (key === 'hasDealRoom') return; // exclude
      if (value === null || value === '' || value === undefined) return; // skip empty
      obj[key] = value;
    });
    return obj;
  };

  const handleSubmit = () => { // submit
    const searchParams = new URLSearchParams(location.search);
    const existingParams = Object.fromEntries(searchParams.entries());

    const pipelines = filterOptions?.pipelines; // get the pipelines

    const newParams = getFilterParams(values);
    const mergeParams = {
      ...existingParams,
      ...newParams,
      'pipelineExternalId': values?.pipelineExternalId ?? pipelines?.[0]?.externalId ?? '', // always set the pipelineExternalId as default
    };

    console.log("mergeParams", mergeParams);

    const url = buildUrl(location.pathname, { queryParams: mergeParams });
    navigate(url); // update the query
    onClose(); // close
  };

  const handleReset = () => { // reset the filter values
    const { riskStatus, ...rest } = BulkFiltersDefaultValues;
    const searchParams = new URLSearchParams(location.search);
    Object.keys(rest).forEach((key) => {
      if (searchParams.has(key)) {
        searchParams.delete(key); // remove the key
      }
    });
    const queryString = searchParams.toString();
    const url = queryString ? `${location.pathname}?${queryString}` : location.pathname;
    navigate(url);

    setValues((prev) => {
      const { riskStatus, ...rest } = prev;
      return {
        ...BulkFiltersDefaultValues,
        riskStatus,
      }
    });
  };

  return (
    <Slider.Dialog
      show={isOpen}
      width="380px"
      onClose={onClose}
      className={styles.sliderContainer}
    >
      <Slider.Header>
        <div className={styles.header}>
          <p className={styles.title}>filters</p>
          <Actions.Button
            width={2}
            className={styles.resetBtn}
            onClick={handleReset}
            title="Reset"
            icon={cx('paperflite-reload', styles.icon)}
          />
        </div>
      </Slider.Header>

      <Slider.Body className={styles.sliderbody}>
        <Fields initialValue={values} onChange={handleChange} options={filterOptions} onValidationChange={(val) => setIsInvalid(val)} />
        <div className={styles.bottomOverlay} />
      </Slider.Body>

      <Slider.Actions>
        <Button variant="primary" icon='paperflite-tick' disabled={isInvalid} text={'Confirm'} onClick={handleSubmit}></Button>
        <Button variant="secondary" text="Cancel" onClick={() => onClose()}></Button>
      </Slider.Actions>

    </Slider.Dialog>
  );
};

// Single Select
interface SingleValueDDProps<T> {
  selected?: T | null;
  options: T[];
  onChange: (option: T | null) => void;
  getValue: (option: T) => any;
  getLabel: (option: T) => string | any;
  allowClear?: boolean;
  loadingOptions?: boolean;
  placeholder?: string;
}

const SingleValueDropDown = <T,>({
  selected,
  options,
  onChange,
  getValue,
  getLabel,
  allowClear = false,
  loadingOptions = false,
  placeholder = 'select'
}: SingleValueDDProps<T>) => {
  // Find selected option from options list
  const selectedOption = useMemo(() => {
    if (!selected) return null;
    return options.find(
      (option) => getValue(option) === getValue(selected)
    ) || null;
  }, [selected, options, getValue]);

  const handleSelect = (option: T) => {
    const isSame = selectedOption && getValue(option) === getValue(selectedOption);
    if (isSame && allowClear) {
      onChange(null);
      return;
    }
    onChange(option);
  };
  return (
    <Dropdown autoClose="outside" className={styles.singleValueDropDown} >

      <Dropdown.Toggle as="div" className={cx(styles.dropdownToggle, {
        [styles.disabled]: loadingOptions,
      })}>
        {selectedOption ? (
          <>
            <span>{getLabel(selectedOption)}</span>
          </>
        ) : (
          <span className={styles.placeholder}> {placeholder}</span>
        )}
        <i className={cx("paperflite-dropdown")} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 6],
              },
            },
          ],
        }}
        className={styles.dropdownMenu}
      >

        <div className={styles.innerContainer}>
          {size(options) > 0 &&
            <>
              {!loadingOptions ? (
                map(options, (option) => {
                  const isSelected =
                    selectedOption &&
                    getValue(option) === getValue(selectedOption);
                  return (
                    <Dropdown.Item
                      key={String(getValue(option))}
                      className={cx(styles.dropdownItem, { [styles.active]: isSelected })}
                      onClick={() => handleSelect(option)}
                    >
                      {isSelected ? (
                        <i className="paperflite-tick-selected" />
                      ) : (
                        <i className="paperflite-radio-button-unselected" />
                      )}
                      <span>{getLabel(option)}</span>
                    </Dropdown.Item>
                  );
                })
              ) : (
                <div className={styles.loading}>Loading...</div>
              )}
            </>}
        </div>
      </Dropdown.Menu>

    </Dropdown >
  );
};

// Date Picker
type Props = {
  from?: any;
  to?: any;
  onChange: (value: { from?: any; to?: any }) => void;
};

const DateRangeInput: React.FC<Props> = ({
  from,
  to,
  onChange,
}) => {
  const format = (value?: any) => value ? moment(value).format('DD/MM/YYYY') : '';


  useEffect(() => { // check the from date is greater than to date
    if (from && to && moment(to).isBefore(from, "day")) {
      onChange({ 'to': from });
    }
  }, [from]);

  return (
    <div className={styles.datePicker}>
      {/* FROM */}
      <DatePicker
        maxDate={moment().endOf("day").toDate()}
        minDate={moment().subtract(100, "years").toDate()}
        customInput={
          <div className={styles.dateDisplay} >
            <p>From</p>
            <i className="paperflite-dropdown"></i>
            <p>{format(from)}</p>
          </div>
        }
        dateFormat="dd MMM yyyy"
        value={format(from)}
        onChange={(val) => onChange({ from: val ? moment(val).local().endOf("day").toISOString(true) : null })}
        selected={from ? moment(from).toDate() : null}
        showYearDropdown
        yearDropdownItemNumber={15}
        scrollableYearDropdown
        portalId={"root-portal"}
      />

      <span className={styles.divider}></span>

      {/* TO */}
      <DatePicker
        maxDate={moment().endOf("day").toDate()}
        minDate={from
          ? moment(from).startOf("day").toDate()
          : moment().subtract(100, "years").toDate()}
        customInput={
          <div className={styles.dateDisplay} >
            <p>To</p>
            <i className="paperflite-dropdown"></i>
            <p>{format(to)}</p>
          </div>
        }
        dateFormat="dd MMM yyyy"
        value={format(to)}
        onChange={(val) => onChange({ 'to': val ? moment(val).local().endOf("day").toISOString(true) : null })}
        selected={to ? moment(to).toDate() : null}
        showYearDropdown
        yearDropdownItemNumber={15}
        scrollableYearDropdown
        portalId={"root-portal"}
      />
    </div>
  );
};

// Multiple select
type BaseOption = Record<string, any>;

interface SelectProps<T extends BaseOption> {
  selected?: T | T[] | null;
  options: T[];
  onChange: (value: T | T[] | null) => void;

  getValue: (item: T) => any;
  getLabel: (item: T) => string;

  isMulti?: boolean;
  allowClear?: boolean;
  loadingOptions?: boolean;
  placeholder?: string;
}

function CustomSelect<T extends BaseOption>({
  selected,
  options,
  onChange,
  getValue,
  getLabel,
  isMulti = false,
  allowClear = false,
  loadingOptions = false,
  placeholder = "Select",
}: SelectProps<T>) {

  const normalizedOptions = map(options, (opt) => ({
    value: getValue(opt),
    label: getLabel(opt),
    raw: opt,
  }));

  const normalizedValue = (() => {
    if (!selected) return isMulti ? [] : null;

    if (isMulti) {
      return map((selected as T[]), (item) => ({
        value: getValue(item),
        label: getLabel(item),
        raw: item,
      }));
    }

    return {
      value: getValue(selected as T),
      label: getLabel(selected as T),
      raw: selected,
    };
  })();

  // styles
  const getStyles = () => {
    return {
      container: (base: any) => ({
        ...base,
        width: "100%",
      }),

      control: (base: any, state: any) => ({
        ...base,
        padding: "12px",
        background: "none",
        borderRadius: "10px",
        outLine: "none",
        boxShadow: 0,
        width: "100%",
        border: "1px solid #DBDBDB",

        "&:hover": {
          boxShadow: 0,
        },

        "> div:first-child": {
          padding: "0px",
          flexWrap: "nowrap",
          overflowX: "auto",
          scrollbarWidth: "none",

          "&::-webkit-scrollbar": {
            display: "none",
          },

          "> div": {
            minWidth: "auto",
          }
        }
      }),

      menu: (base: any) => ({
        ...base,
        padding: "1rem",
        borderRadius: "1rem",
        border: "none",
        backgroundColor: "#FFF",
        width: "90%",
        marginLeft: "10px",
      }),

      placeholder: (base: any) => ({
        ...base,
        padding: "0px",
        fontFamily: "Gilroy, sans-serif",
        fontSize: "11px",
        fontStyle: "normal",
        fontWeight: "500",
        color: "#717274",
        textTransform: "capitalize",
      }),

      singleValue: (base: any) => ({
        ...base,
        width: "fit-content",
        padding: "0px",
        fontFamily: "Gilroy, sans-serif",
        fontSize: "11px",
        fontStyle: "normal",
        fontWeight: "500",
        color: "#060606",

        ":hover": {
          color: "#060606",
          background: "none",
        },
      }),
      multiValue: (styles, { data }) => {
        return {
          ...styles,
          backgroundColor: "transparent",
          borderRadius: "50px",
          border: "1px solid #DBDBDB",
          padding: "8px",
        };
      },
      multiValueLabel: (styles, { data }) => ({
        ...styles,
        color: "#060606",
        fontFamily: "Gilroy, sans-serif",
        fontSize: "11px",
        fontWeight: "500",
        width: "fit-content",
        padding: "0px",
        paddingLeft: "6px",
      }),
      multiValueRemove: (styles, { data }) => ({
        ...styles,
        paddingRight: "0px",
        "svg": {
          color: "#717274",
          backgroundColor: "transparent",
          borderRadius: "50%",
          border: "1px solid #DBDBDB",
        },

        ':hover': {
          backgroundColor: "transparent",
          color: '#717274',
        },
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        padding: "0px",
      }),
      indicatorSeparator: (base: any) => ({
        ...base,
        alignSelf: "auto",
      }),
      dropdownIndicator: (base: any, state: any) => ({
        ...base,
        // transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
        color: "#717274",
        padding: "0px",
        "&:hover": {
          color: "#717274",
        },
        svg: {
          height: "16px",
          width: "16px",
        },
      }),
    };
  };

  const baseStyles = getStyles();

  const customStyles = {
    ...baseStyles,
    singleValue: (provided: any) => {
      const original = baseStyles.singleValue(provided);
      return {
        ...original,
        "&:hover": {
          background: "none",
        },
      };
    },
  };

  return (
    <Select
      options={normalizedOptions}
      value={normalizedValue}
      isMulti={isMulti}
      isClearable={allowClear}
      isLoading={loadingOptions}
      isSearchable={false}
      styles={customStyles}
      placeholder={placeholder}
      isDisabled={loadingOptions}
      onChange={(option) => {
        if (isMulti) {
          const values = map((option as MultiValue<any>), (o) => o.raw);
          onChange(values || []);
        } else {
          const value = (option as SingleValue<any>)?.raw || null;
          onChange(value);
        }
      }}
      menuShouldScrollIntoView={false}
      components={{
        Option: (props: any) => {
          const { innerProps, isSelected, label } = props;
          return (
            <div className={styles.customOptions} {...innerProps}>
              {isSelected ?
                <i className={cx("paperflite-tick-selected", styles.selected)} />
                : <i className={cx("paperflite-radio-button-unselected")} />
              }
              <p className={styles.item}>{label}</p>
            </div>
          );
        },

        NoOptionsMessage: (props: any) => {
          const { children, innerProps } = props;

          return (
            <div {...innerProps}>
              <span className={styles.item}>{children}</span>
            </div>
          );
        },
      }}
    />
  );
}

export default FiltersSlider;
